import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export class PccImmunizationParams {
	given?: boolean;

	createdStartDateTime?: string;

	createdEndDateTime?: string;

	administrationStartDateTime?: string;

	administrationEndDateTime?: string;
}

/**
 * Represents the amount administered for an immunization
 */
export class PccAdministeredAmount {
	/**
	 * Represents the unit of the immunization that was administered to the patient, if recorded
	 */
	unit: string;

	/**
	 * Represents the amount of the immunization administered to the patient, if recorded
	 */
	value: number;
}

/**
 * Represents an immunization record for a patient in PointClickCare
 *
 * SimpliScreen maps the immunization to the following string for embedding in the RAG:
 * immunization: {immunization}, given: {given}, consentStatus: {consentStatus}, administrationDateTime: {administrationDateTime}, cvxCode: {cvxCode}, cvxDescription: {cvxDescription}, locationGiven: {locationGiven}, routeOfAdministration: {routeOfAdministration}, manufacturerName: {manufacturerName}, lotNumber: {lotNumber}, substanceExpirationDate: {substanceExpirationDate}, results: {results}, induration: {induration}, notes: {notes}, amountAdministered: {amountAdministered.value}{amountAdministered.unit}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/immunizations?patientId={patientId}
 */
export class PccImmunization extends PccAuditInfo {
	/**
	 * Represents the name and login name of the PointClickCare user who administered the immunization to the patient
	 */
	administeredBy?: string;

	/**
	 * Represents the date and time when the immunization was administered to the patient. This field is present if 'given' is set to true. The date is expressed in UTC in the following format: YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	administrationDateTime?: string;

	/**
	 * Represents the amount administered for the immunization, if recorded
	 */
	amountAdministered?: PccAdministeredAmount;

	/**
	 * Represents the login name for the PointClickCare user who recorded the patient's consent for the immunization
	 */
	consentConfirmedBy: string;

	/**
	 * Represents the date when the patient or their family were educated with regards to the immunization. The date is expressed in UTC in the following format:YYYY-MM-DD.
	 */
	consentConfirmedDate: string;

	/**
	 * Represents the patient's consent for the immunization. Possible values are 'TBD', 'Consented', 'Refused', 'Historical', 'notEligible'.
	 */
	consentStatus: 'TBD' | 'Consented' | 'Refused' | 'Historical' | 'notEligible';

	/**
	 * Represents the CVX code for the immunization if defined, if available
	 */
	cvxCode?: string;

	/**
	 * Represents the full-name vaccine name for the immunization if defined, if available
	 */
	cvxDescription?: string;

	/**
	 * Represents whether if the patient or their family have been educated with regards to the immunization
	 */
	educationProvided: boolean;

	/**
	 * Represents who provided the patient or their family education with regards to the immunization
	 */
	educationProvidedBy?: string;

	/**
	 * Represents the date when the patient or their family were educated with regards to the immunization. The date is expressed in UTC in the following format:YYYY-MM-DD.
	 */
	educationProvidedDate?: string;

	/**
	 * Represents if an immunization was administered or not. If this field is 'true', then administration date and time will be present.
	 */
	given: boolean;

	/**
	 * Represents the immunization
	 */
	immunization: string;

	/**
	 * The unique identifier for the immunization record
	 */
	immunizationId: number;

	/**
	 * Represents the recorded induration in millimeters (mm) for immunizations that require results to be tracked, i.e. trackResults is 'true'.
	 */
	induration: number;

	/**
	 * Represents the location where the immunization was administered. Possible values are 'Left Deltoid', 'Right Deltoid', 'Left Forearm', 'Right Forearm', 'Left Hip', 'Right Hip', 'Left Buttock', 'Right Buttock', 'Left Thigh', 'Right Thigh'.
	 */
	locationGiven?: string;

	/**
	 * Represents the immunization's lot number found on the immunization label or accompanying packaging, if recorded
	 */
	lotNumber?: string;

	/**
	 * Represents the name of the immunizations manufacturer, if recorded
	 */
	manufacturerName?: string;

	/**
	 * Represents if the vaccination is to be administered in multiple steps. This is true for 'TB 2 Step Mantoux Skin Test'.
	 */
	multiStep: boolean;

	/**
	 * Represents if the next step for the vaccination is required or not. This field is only applicable for immunizations that are multi step.
	 */
	nextStepRequired?: boolean;

	/**
	 * Free text notes entered for the immunization record. Notes can be added at the time of obtaining consent from the patient, administering an immunization, or even later.
	 */
	notes?: string;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	patientId: number;

	/**
	 * Represents the reason the patient is not eligible for the immunization. This field is present if 'consentStatus' is 'notEligible'. Possible values are:
	 * 'Admitted after flu season', 'Allergies', 'Contraindicated', 'Had Previously / Not Due', 'Illness / Medical Condition', 'MD Refused', 'No consent', 'Not offered', 'Vaccine Unavailable'
	 */
	reasonNotEligible?: string;

	/**
	 * Represents the reason the immunization was refused. This field is present if 'consentStatus' is 'Refused'. Possible values are:
	 * 'Family Refused', 'POA Refused', 'Religious Beliefs', 'Resident Refused', 'Vaccine Safety Concerns', 'Vaccine Efficacy Concerns', 'Philosophical Objection'
	 */
	reasonRefused?: string;

	/**
	 * Represents the recorded results after administering an immunization that requires results to be tracked. Possible values are 'Positive', 'Negative' and 'Unable to Determine'.
	 */
	results?: string;

	/**
	 * Represents if results are pending for an administered immunization that requires results to be tracked.
	 */
	resultsPending?: boolean;

	/**
	 * Represents the route by which the immunization was administered to the patient, if recorded. If recorded, the possible values are 'by mouth', 'in both nostrils', 'in each nostril', 'intradermally', 'intramuscularly', 'orally','subcutaneously'.
	 */
	routeOfAdministration?: string;

	/**
	 * Represents the step for the immunization record. This is only applicable for TB 2 Step Mantoux Skin Test which is a multi-step immunization, i.e. 'multiStep' is true.
	 */
	step?: number;

	/**
	 * Represents the expiration date for the immunization, if recorded. The date is expressed in UTC in the following format: YYYY-MM-DD.
	 */
	substanceExpirationDate?: string;

	/**
	 * Represents if the immunization requires results to be tracked. This is true for TB vaccines.
	 */
	trackResults: boolean;

	/**
	 * Represents the details of the immunization.
	 *
	 * SimpliScreen maps the immunization to the following string for embedding in the RAG:
	 * immunization: {immunization}, given: {given}, consentStatus: {consentStatus}, administrationDateTime: {administrationDateTime}, cvxCode: {cvxCode}, cvxDescription: {cvxDescription}, locationGiven: {locationGiven}, routeOfAdministration: {routeOfAdministration}, manufacturerName: {manufacturerName}, lotNumber: {lotNumber}, substanceExpirationDate: {substanceExpirationDate}, results: {results}, induration: {induration}, notes: {notes}, amountAdministered: {amountAdministered.value}{amountAdministered.unit}
	 */
	details: string;

	constructor(partial?: Partial<PccImmunization>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails(skipName = false) {
		const details = [`Data Type: Immunization`];
		if (this.immunizationId) details.push(`Immunization ID: ${this.immunizationId}`);
		if (this.immunization && !skipName) details.push(`Immunization: ${this.immunization}`);
		if (this.given) details.push(`Given: ${this.given}`);
		if (this.consentStatus) details.push(`Consent Status: ${this.consentStatus}`);
		if (this.administrationDateTime) details.push(`Administration Date Time: ${this.administrationDateTime}`);
		if (this.cvxCode) details.push(`CVX Code: ${this.cvxCode}`);
		if (this.cvxDescription) details.push(`CVX Description: ${this.cvxDescription}`);
		if (this.locationGiven) details.push(`Location Given: ${this.locationGiven}`);
		if (this.routeOfAdministration) details.push(`Route Of Administration: ${this.routeOfAdministration}`);
		if (this.manufacturerName) details.push(`Manufacturer Name: ${this.manufacturerName}`);
		if (this.lotNumber) details.push(`Lot Number: ${this.lotNumber}`);
		if (this.substanceExpirationDate) details.push(`Substance Expiration Date: ${this.substanceExpirationDate}`);
		if (this.results) details.push(`Results: ${this.results}`);
		if (this.induration) details.push(`Induration: ${this.induration}`);
		if (this.notes) details.push(`Notes: ${this.notes}`);
		if (this.amountAdministered) details.push(`Amount Administered: ${this.amountAdministered.value}${this.amountAdministered.unit ?? ''}`);
		return details.join(', ');
	}
}
